/** A review summary's record sections, rendered generically (client MCP plan §4.6 stage 5, §4.7
 *  "Batches"; backend `demo.lib.ai_edit.review_summary`, WPS P3-16 / FE-07).
 *
 *  A summary — a proposal review's `request.preview_summary`, a pending change's `summary` — says
 *  its `domain` and lists its record `sections`: the key holding the records, the kind in words,
 *  the field that ids a record and the one that names it, and the fields that hold the company's
 *  or its AI's text (`untrusted_fields`). This view reads only that description, so a content kind
 *  added later (or the vocabulary, or a batch revert's `roots`) needs no change here.
 *
 *  - The untrusted fields are quoted plain text (`UntrustedInline`) under ONE legend — the
 *    summary's own — never markup, never instructions; the rest are our words.
 *  - A large change (`per_root_summary`) lists one line per record first — its kind, name,
 *    operation, the fields it changes, its warnings — each opening its diff: not a wall of diffs.
 *  - A record copied from a franchise parent the viewer may not read arrives `redacted`: the
 *    backend's note, the operation and the fields, never the parent's values.
 *  - A batch revert's `roots` line links the change set it reverts; that page has the records.
 *
 *  The summary is JSON an earlier preview stored: every part is read defensively, and a part
 *  missing leaves the page — the only place to apply — standing. A notification summary has no
 *  sections (its `settings` and `franchise` have their own views): nothing renders here, but for
 *  a revert's plan (`plan_summary`). */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Fragment, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PLAN_SUMMARY_WORDS, shortId, statusLabel } from '@/api/aiChanges';
import type { AIProposalPreviewSummary, AIReviewSection } from '@/api/aiEdit';
import { SectionCard } from '@/components/ReadView';
import { UntrustedInline, UntrustedLegend } from '@/components/UntrustedText';

import { aiChangeHref } from './aiChangeLinks';

type Rec = Record<string, unknown>;

const isRecord = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const records = (v: unknown): Rec[] => (Array.isArray(v) ? v.filter(isRecord) : []);
const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The keys every summary holds besides the record fields: never rendered as "other words". */
const RECORD_FRAME = new Set(['changes', 'after', 'before']);

const OP_WORDS: Record<string, string> = { create: 'New', update: 'Update', delete: 'Delete' };
const opWord = (op: unknown) => (typeof op === 'string' ? (OP_WORDS[op] ?? op) : '—');

/** A stored value as text: a string as it is, anything else as its JSON. */
function valueText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

/** One value: quoted when its field holds the company's or its AI's text, else our words. */
function Value({ value, untrusted }: { value: unknown; untrusted: boolean }) {
  const text = valueText(value);
  if (untrusted) return <UntrustedInline text={text} />;
  return (
    <Typography variant="body2" component="span" color={text ? undefined : 'text.disabled'}>
      {text || '—'}
    </Typography>
  );
}

/** How a section reads its records (the backend's description, with safe defaults). */
interface Reader {
  section: AIReviewSection;
  untrusted: (field: string) => boolean;
  name: (r: Rec) => unknown;
  id: (r: Rec) => unknown;
  fields: (r: Rec) => string[];
}

function readerOf(section: AIReviewSection): Reader {
  const untrustedFields = new Set(strings(section.untrusted_fields));
  const labelField = section.label_field || 'label';
  const idField = section.id_field || 'id';
  return {
    section,
    untrusted: (field) => untrustedFields.has(field),
    // A new record's name lives in its `after`, a deleted one's in its `before`.
    name: (r) =>
      r[labelField] ??
      (isRecord(r.after) ? r.after[labelField] : undefined) ??
      (isRecord(r.before) ? r.before[labelField] : undefined),
    id: (r) => r[idField],
    fields: (r) => {
      const listed = strings(r.fields);
      if (listed.length || Array.isArray(r.fields)) return listed;
      if (isRecord(r.changes)) return Object.keys(r.changes);
      if (isRecord(r.after)) return Object.keys(r.after);
      return [];
    },
  };
}

function RecordName({ reader, r }: { reader: Reader; r: Rec }) {
  const name = valueText(reader.name(r));
  const field = reader.section.label_field || 'label';
  if (!name) {
    return (
      <Typography variant="body2" component="span" color="text.secondary">
        {r.redacted ? 'Not shown' : 'Unnamed'}
      </Typography>
    );
  }
  return reader.untrusted(field) ? (
    <UntrustedInline text={name} />
  ) : (
    <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
      {name}
    </Typography>
  );
}

function RecordId({ reader, r }: { reader: Reader; r: Rec }) {
  const id = reader.id(r);
  if (typeof id !== 'number' && typeof id !== 'string') return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {`#${id}`}
    </Typography>
  );
}

/** Our flags on a record: a copy of the parent's, its warnings, the fields a sync manages. */
function RecordChips({ r }: { r: Rec }) {
  const warnings = count(r.warnings);
  const managed = strings(r.managed);
  return (
    <>
      <Chip size="small" variant="outlined" label={opWord(r.op)} />
      {r.copy_of_parent === true && (
        <Chip size="small" variant="outlined" color="info" label="Copy of the parent’s" />
      )}
      {warnings > 0 && (
        <Chip
          size="small"
          variant="outlined"
          color="warning"
          label={plural(warnings, 'warning', 'warnings')}
        />
      )}
      {managed.length > 0 && (
        <Chip size="small" variant="outlined" color="warning" label="Managed by a sync" />
      )}
    </>
  );
}

/** `{field: {before, after}}` as Field · Before · After. */
function ChangesTable({ title, changes, untrusted }: { title: string; changes: Rec; untrusted: boolean }) {
  return (
    <Table size="small" aria-label={title} sx={{ mb: 1, '& td, & th': { verticalAlign: 'top' } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: '24%' }}>Field</TableCell>
          <TableCell sx={{ width: '38%' }}>Before</TableCell>
          <TableCell sx={{ width: '38%' }}>After</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(changes).map(([field, change]) => {
          const c = isRecord(change) ? change : {};
          return (
            <TableRow key={field}>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-word' }}>
                {field}
              </TableCell>
              <TableCell>
                <Value value={c.before} untrusted={untrusted} />
              </TableCell>
              <TableCell>
                <Value value={c.after} untrusted={untrusted} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** A new record as it would read, or a deleted one as it read: Field · Value. */
function ValuesTable({ title, values, untrusted }: { title: string; values: Rec; untrusted: boolean }) {
  return (
    <Table size="small" aria-label={title} sx={{ mb: 1, '& td, & th': { verticalAlign: 'top' } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: '24%' }}>Field</TableCell>
          <TableCell>Value</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(values).map(([field, value]) => (
          <TableRow key={field}>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-word' }}>
              {field}
            </TableCell>
            <TableCell>
              <Value value={value} untrusted={untrusted} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** What one record changes: the notes, its warnings, then its diff. */
function RecordDetail({
  reader,
  r,
  warnings,
}: {
  reader: Reader;
  r: Rec;
  warnings: string[];
}) {
  const nameText = valueText(reader.name(r)) || `Item #${valueText(r.item)}`;
  const managed = strings(r.managed);
  const shortened = strings(r.shortened);
  const labelField = reader.section.label_field || 'label';
  // The record's other untrusted words (a vocabulary entry's `binding_names`, …).
  const others = strings(reader.section.untrusted_fields).filter(
    (f) => f !== labelField && !RECORD_FRAME.has(f) && r[f] !== undefined && r[f] !== null,
  );
  return (
    <Box data-review-record-detail>
      {r.redacted === true && typeof r.note === 'string' && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {r.note}
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }} data-review-warnings>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {warnings.map((w, i) => (
              // Warnings carry no id; their order is the preview's.
              // eslint-disable-next-line react/no-array-index-key
              <Typography key={`${i}:${w}`} component="li" variant="body2">
                {w}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}
      {managed.length > 0 && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }} data-review-managed>
          {`Managed by the booking system’s sync — the next sync from the booking system overwrites: ${managed.join(', ')}.`}
        </Typography>
      )}
      {others.map((f) => (
        <Box key={f} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" component="span" sx={{ mr: 1 }}>
            {f}
          </Typography>
          {Array.isArray(r[f]) ? (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" component="span">
              {(r[f] as unknown[]).map((v, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <UntrustedInline key={i} text={valueText(v)} />
              ))}
            </Stack>
          ) : (
            <UntrustedInline text={valueText(r[f])} />
          )}
        </Box>
      ))}
      {isRecord(r.changes) && Object.keys(r.changes).length > 0 && (
        <ChangesTable
          title={`${nameText}: before → after`}
          changes={r.changes}
          untrusted={reader.untrusted('changes')}
        />
      )}
      {isRecord(r.after) && (
        <ValuesTable
          title={`${nameText}: new record`}
          values={r.after}
          untrusted={reader.untrusted('after')}
        />
      )}
      {isRecord(r.before) && (
        <ValuesTable
          title={`${nameText}: deleted record`}
          values={r.before}
          untrusted={reader.untrusted('before')}
        />
      )}
      {shortened.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="div">
          {`Shortened in this summary: ${shortened.join(', ')}.`}
        </Typography>
      )}
    </Box>
  );
}

const recordKey = (r: Rec, i: number) => (typeof r.item === 'number' ? r.item : i);

/** Every record in full: a few records, nothing to fold. */
function RecordBlocks({
  reader,
  rows,
  warningsOf,
}: {
  reader: Reader;
  rows: Rec[];
  warningsOf: (r: Rec) => string[];
}) {
  return (
    <Stack spacing={2}>
      {rows.map((r, i) => {
        const fields = reader.fields(r);
        return (
          <Box key={recordKey(r, i)} data-review-record={recordKey(r, i)}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
              <RecordName reader={reader} r={r} />
              <RecordId reader={reader} r={r} />
              <RecordChips r={r} />
              {fields.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {`Fields: ${fields.join(', ')}`}
                </Typography>
              )}
            </Stack>
            <RecordDetail reader={reader} r={r} warnings={warningsOf(r)} />
          </Box>
        );
      })}
    </Stack>
  );
}

/** One line per record first, each opening its diff (`per_root_summary`). */
function RecordLines({
  reader,
  rows,
  warningsOf,
}: {
  reader: Reader;
  rows: Rec[];
  warningsOf: (r: Rec) => string[];
}) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (k: number) =>
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const showKind = reader.section.kind === null;
  const columns = showKind ? 6 : 5;
  return (
    <Table size="small" aria-label={`${reader.section.label}: one line per record`}>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" />
          {showKind && <TableCell>Kind</TableCell>}
          <TableCell>Name</TableCell>
          <TableCell>Operation</TableCell>
          <TableCell>Fields</TableCell>
          <TableCell>Notes</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => {
          const k = recordKey(r, i);
          const expanded = open.has(k);
          const nameText = valueText(reader.name(r)) || `item #${valueText(r.item)}`;
          const warnings = count(r.warnings);
          return (
            <Fragment key={k}>
              <TableRow data-review-record={k}>
                <TableCell padding="checkbox">
                  <IconButton
                    size="small"
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Hide' : 'Show'} the diff of ${nameText}`}
                    onClick={() => toggle(k)}
                  >
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </TableCell>
                {showKind && <TableCell>{valueText(r.kind) || '—'}</TableCell>}
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <RecordName reader={reader} r={r} />
                    <RecordId reader={reader} r={r} />
                  </Stack>
                </TableCell>
                <TableCell>{opWord(r.op)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-word' }}>
                  {reader.fields(r).join(', ') || '—'}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {r.copy_of_parent === true && (
                      <Chip size="small" variant="outlined" color="info" label="Copy of the parent’s" />
                    )}
                    {warnings > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        label={plural(warnings, 'warning', 'warnings')}
                      />
                    )}
                    {strings(r.managed).length > 0 && (
                      <Chip size="small" variant="outlined" color="warning" label="Managed by a sync" />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
              {expanded && (
                <TableRow>
                  <TableCell colSpan={columns} sx={{ bgcolor: 'background.default' }}>
                    <RecordDetail reader={reader} r={r} warnings={warningsOf(r)} />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** A batch revert's records: one line per change set it reverts (no diff of its own here). */
function RootLines({ reader, rows }: { reader: Reader; rows: Rec[] }) {
  return (
    <Table size="small" aria-label={reader.section.label}>
      <TableHead>
        <TableRow>
          <TableCell>Kind</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>The apply</TableCell>
          <TableCell>Change set</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Records</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => {
          const cs = typeof r.change_set === 'string' ? r.change_set : '';
          return (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={`${cs}:${i}`} data-review-record={i}>
              <TableCell>{valueText(r.kind) || '—'}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <RecordName reader={reader} r={r} />
                  <RecordId reader={reader} r={r} />
                </Stack>
              </TableCell>
              <TableCell>{opWord(r.op)}</TableCell>
              <TableCell>
                {cs ? (
                  <Link component={RouterLink} to={aiChangeHref(cs)} underline="hover">
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                      {shortId(cs)}
                    </Box>
                  </Link>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>{typeof r.status === 'string' ? statusLabel(r.status) : '—'}</TableCell>
              <TableCell align="right">{count(r.entries)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function Section({
  section,
  summary,
  perRoot,
  warningsOf,
}: {
  section: AIReviewSection;
  summary: Rec;
  perRoot: boolean;
  warningsOf: (r: Rec) => string[];
}) {
  const reader = readerOf(section);
  const rows = records(summary[section.key]);
  const isRoots = section.key === 'roots';
  let body: ReactNode;
  if (rows.length === 0) {
    body = (
      <Typography variant="body2" color="text.secondary">
        No record is listed.
      </Typography>
    );
  } else if (isRoots) {
    body = <RootLines reader={reader} rows={rows} />;
  } else if (perRoot) {
    body = <RecordLines reader={reader} rows={rows} warningsOf={warningsOf} />;
  } else {
    body = <RecordBlocks reader={reader} rows={rows} warningsOf={warningsOf} />;
  }
  return (
    <Box component="section" data-review-section={section.key} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
        {`${section.label || section.key} · ${count(section.records) || rows.length}`}
      </Typography>
      {body}
    </Box>
  );
}

/** "departures: 4 · bookings: 12" — a count block as it is stored. */
function countsText(v: unknown): string {
  if (!isRecord(v)) return '';
  return Object.entries(v)
    .filter(([, n]) => typeof n === 'number' || typeof n === 'string' || typeof n === 'boolean')
    .map(([k, n]) => `${k.replace(/_/g, ' ')}: ${String(n)}`)
    .join(' · ');
}

/** The facts of a summary around its records: counts, a revert's plan, what is not listed. */
function Facts({ summary }: { summary: Rec }) {
  const lines: { key: string; text: string }[] = [];
  const blast = countsText(summary.blast_radius);
  if (blast) lines.push({ key: 'blast', text: `Blast radius: ${blast}` });
  const exposure = countsText(summary.guest_exposure);
  if (exposure) lines.push({ key: 'exposure', text: `Guest exposure: ${exposure}` });
  if (isRecord(summary.plan_summary)) {
    const plan = Object.entries(summary.plan_summary)
      .filter(([, n]) => count(n) > 0)
      .map(([k, n]) => `${count(n)} ${PLAN_SUMMARY_WORDS[k] ?? k}`)
      .join(' · ');
    const entries = count(summary.entries);
    const sets = count(summary.change_sets);
    const scale = [
      entries ? plural(entries, 'record', 'records') : '',
      sets ? `in ${plural(sets, 'change set', 'change sets')}` : '',
    ]
      .filter(Boolean)
      .join(' ');
    lines.push({
      key: 'plan',
      text: `The plan the requester saw: ${[plan, scale].filter(Boolean).join(' · ') || 'nothing to do'}`,
    });
  }
  if (lines.length === 0) return null;
  return (
    <Box data-review-facts sx={{ mb: 1 }}>
      {lines.map((l) => (
        <Typography key={l.key} variant="body2">
          {l.text}
        </Typography>
      ))}
    </Box>
  );
}

/** Does this summary describe record sections (the generic view has something to show)? */
export function hasReviewSections(summary: AIProposalPreviewSummary | null | undefined): boolean {
  return Array.isArray(summary?.sections) && summary.sections.some((s) => isRecord(s) && s.key);
}

export function ReviewSummarySections({
  summary,
  title = 'Records',
  bare = false,
}: {
  summary: AIProposalPreviewSummary | null | undefined;
  /** The card's title (`bare`: none — inside a pending change's card). */
  title?: string;
  bare?: boolean;
}) {
  const rec = (summary ?? {}) as Rec;
  // A notification change set's revert has no record sections, only its plan.
  if (!summary || (!hasReviewSections(summary) && !isRecord(rec.plan_summary))) return null;
  const sections = (summary.sections ?? []).filter((s) => isRecord(s) && s.key);
  const itemWarnings = isRecord(rec.item_warnings) ? rec.item_warnings : {};
  const warningsOf = (r: Rec) => strings(itemWarnings[valueText(r.item)]);
  const notShown = count(rec.records_not_shown);
  const body = (
    <Box data-review-summary data-domain={summary.domain}>
      <Facts summary={rec} />
      {summary.content_is_untrusted_data && summary.legend && (
        <UntrustedLegend title="The company’s text and its AI’s" legend={summary.legend} />
      )}
      {sections.map((s) => (
        <Section
          key={s.key}
          section={s}
          summary={rec}
          perRoot={summary.per_root_summary === true}
          warningsOf={warningsOf}
        />
      ))}
      {notShown > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {`${notShown === 1 ? '1 more record is' : `${notShown} more records are`} counted but not listed.`}
        </Typography>
      )}
    </Box>
  );
  return bare ? body : <SectionCard title={title}>{body}</SectionCard>;
}
